/**
 * Modal de configuração para node de coleta de dados
 * Permite definir tipo de dado, variável e pergunta
 *
 * CHANGELOG:
 *   v1.4.0 - 2026-09-07
 *     - Campos que recebem respostas migrados para o modal de destino.
 *   v1.2.0 - 2026-07-23
 *     - Adicionado bloco "Tempo de espera pela resposta" (minutos/horas/dias)
 *       + valor default gravado na variável em caso de timeout. Reaproveita
 *       infra de scheduled_resume_at + flow-scheduler (padrão do Delay).
 *   v1.1.0 - 2026-07-09
 *     - Adicionada seção "Canal de Envio" (Canal ativo / Canal específico) para
 *       viabilizar uso do node em fluxos disparados por gatilhos CRM
 *       (que não possuem inbox no contexto).
 *     - Adicionado toggle "Apenas aguardar resposta (não enviar pergunta)".
 *   v1.0.0 - Versão inicial.
 */

import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { FlowNode, InputDataConfig } from '@/types/flows';
import { z } from 'zod';
import { toast } from 'sonner';
import { HelpCircle, Send, MessageSquare, CheckCircle2, Clock } from 'lucide-react';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariableDestinationPicker } from '../VariableDestinationPicker';
import { TextareaWithVariables } from '../TextareaWithVariables';

// Schema de validação base (sem question — validado condicionalmente conforme wait_only)
const inputDataBaseSchema = z.object({
  variable_name: z.string()
    .trim()
    .min(1, 'Nome da variável é obrigatório')
    .max(50, 'Nome muito longo')
    .regex(/^[a-zA-Z_][a-zA-Z0-9_]*$/, 'Nome inválido (use apenas letras, números e underscore, começando com letra)'),
  data_type: z.enum(['text', 'number', 'email', 'phone', 'cpf', 'cnpj', 'date']),
  validation_message: z.string().trim().max(200, 'Mensagem muito longa').optional(),
});

interface ConfigureInputDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode | null;
  onSave: (nodeId: string, config: InputDataConfig) => void;
  tenantId: string;
}

/**
 * Dialog de configuração de input de dados
 */
export function ConfigureInputDialog({ open, onOpenChange, node, onSave, tenantId }: ConfigureInputDialogProps) {
  const nicknameRef = useRef<NodeIdFieldRef>(null);
  const [variableName, setVariableName] = useState(node?.data.config?.variable_name || '');
  const [dataType, setDataType] = useState<'text' | 'number' | 'email' | 'phone' | 'cpf' | 'cnpj' | 'date'>(
    node?.data.config?.data_type || 'text'
  );
  const [question, setQuestion] = useState(node?.data.config?.question || '');
  const [validationMessage, setValidationMessage] = useState(node?.data.config?.validation_message || '');
  const [showHelp, setShowHelp] = useState(false);

  // RELEASE v1.1.0 - Canal de envio + wait_only
  const [channelMode, setChannelMode] = useState<'active' | 'specific'>(
    node?.data.config?.inbox_id ? 'specific' : 'active'
  );
  const [selectedInboxId, setSelectedInboxId] = useState(node?.data.config?.inbox_id || '');
  const [waitOnly, setWaitOnly] = useState<boolean>(!!node?.data.config?.wait_only);

  // RELEASE v1.2.0 - Timeout de resposta
  const [timeoutEnabled, setTimeoutEnabled] = useState<boolean>(
    !!node?.data.config?.response_timeout_enabled
  );
  const [timeoutValue, setTimeoutValue] = useState<number>(
    Number(node?.data.config?.response_timeout_value) || 10
  );
  const [timeoutUnit, setTimeoutUnit] = useState<'minutes' | 'hours' | 'days'>(
    node?.data.config?.response_timeout_unit || 'minutes'
  );
  // v1.3.0 - Variável global de resultado (recebe 'responded' | 'timeout')
  const [timeoutResultVariable, setTimeoutResultVariable] = useState<string>(
    node?.data.config?.response_timeout_result_variable || ''
  );

  /**
   * Query para buscar inboxes ativas do tenant (mesmo padrão do ConfigureMessageDialog)
   */
  const { data: inboxes = [], isLoading: loadingInboxes } = useQuery({
    queryKey: ['inboxes-selector', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('inboxes')
        .select('id, name, channel_type, identifier')
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .order('name');

      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open,
  });

  /**
   * Sincroniza estados quando node mudar
   */
  useEffect(() => {
    if (node) {
      setVariableName(node.data.config?.variable_name || '');
      setDataType(node.data.config?.data_type || 'text');
      setQuestion(node.data.config?.question || '');
      setValidationMessage(node.data.config?.validation_message || '');
      setChannelMode(node.data.config?.inbox_id ? 'specific' : 'active');
      setSelectedInboxId(node.data.config?.inbox_id || '');
      setWaitOnly(!!node.data.config?.wait_only);
      setTimeoutEnabled(!!node.data.config?.response_timeout_enabled);
      setTimeoutValue(Number(node.data.config?.response_timeout_value) || 10);
      setTimeoutUnit(node.data.config?.response_timeout_unit || 'minutes');
      setTimeoutResultVariable(node.data.config?.response_timeout_result_variable || '');
    }
  }, [node]);

  /**
   * Salva a configuração
   */
  const handleSave = () => {
    if (!node) return;

    try {
      // Validação de canal específico (mesma UX do ConfigureMessageDialog)
      if (channelMode === 'specific' && !selectedInboxId) {
        toast.error('Selecione uma inbox para envio');
        return;
      }

      // Se NÃO for wait_only, exige pergunta preenchida
      if (!waitOnly) {
        const trimmed = (question || '').trim();
        if (trimmed.length === 0) {
          toast.error('A pergunta é obrigatória (ou marque "Apenas aguardar resposta")');
          return;
        }
        if (trimmed.length > 1024) {
          toast.error('Pergunta muito longa');
          return;
        }
      }

      const base = inputDataBaseSchema.parse({
        variable_name: variableName,
        data_type: dataType,
        validation_message: validationMessage || undefined,
      });

      // Validação do timeout (se habilitado)
      if (timeoutEnabled) {
        const v = Number(timeoutValue);
        if (!Number.isFinite(v) || v < 1) {
          toast.error('Tempo de espera deve ser >= 1');
          return;
        }
      }

      // Monta config final. Quando wait_only=true, question fica vazia (não é usada pelo engine).
      const config: InputDataConfig = {
        variable_name: base.variable_name!,
        data_type: base.data_type!,
        validation_message: base.validation_message,
        question: waitOnly ? '' : question.trim(),
        ...(channelMode === 'specific' && selectedInboxId ? { inbox_id: selectedInboxId } : {}),
        ...(waitOnly ? { wait_only: true } : {}),
        ...(timeoutEnabled
          ? {
              response_timeout_enabled: true,
              response_timeout_value: Math.floor(Number(timeoutValue)),
              response_timeout_unit: timeoutUnit,
              ...(timeoutResultVariable ? { response_timeout_result_variable: timeoutResultVariable } : {}),
            }
          : {}),
      };

      onSave(node.id, { ...config, nickname: nicknameRef.current?.getNickname() } as InputDataConfig);
      toast.success('Configuração salva com sucesso');
      onOpenChange(false);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.errors[0];
        toast.error(firstError.message);
      } else {
        toast.error('Erro ao salvar configuração');
      }
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Configurar Coleta de Dados</DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Campo ID do Node (somente leitura) */}
            <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

            <div className="space-y-2">
              <Label>Variável para Salvar Resposta *</Label>
              <VariableDestinationPicker
                tenantId={tenantId}
                value={variableName}
                valueName={variableName}
                onChange={(variable) => {
                  setVariableName(variable?.name || '');
                  if (variable && ['text', 'number', 'email', 'phone', 'cpf', 'cnpj', 'date'].includes(variable.type)) {
                    setDataType(variable.type as any);
                  }
                }}
                placeholder="Selecione ou crie uma variável"
              />
              <p className="text-xs text-muted-foreground">
                A resposta do usuário será salva nesta variável
              </p>
            </div>

            <div className="space-y-2">
              <Label>Tipo de Dado *</Label>
              <Select value={dataType} onValueChange={(v: any) => setDataType(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="text">Texto</SelectItem>
                  <SelectItem value="number">Número</SelectItem>
                  <SelectItem value="email">E-mail</SelectItem>
                  <SelectItem value="phone">Telefone</SelectItem>
                  <SelectItem value="cpf">CPF</SelectItem>
                  <SelectItem value="cnpj">CNPJ</SelectItem>
                  <SelectItem value="date">Data</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* RELEASE v1.1.0 - Seção Canal de Envio (mesmo padrão do ConfigureMessageDialog) */}
            <Separator className="my-2" />
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Send className="h-4 w-4 text-muted-foreground" />
                <Label className="text-sm font-medium">Canal de Envio</Label>
              </div>
              <RadioGroup
                value={channelMode}
                onValueChange={(v: 'active' | 'specific') => setChannelMode(v)}
                className="space-y-2"
              >
                <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                  <RadioGroupItem value="active" id="input-channel-active" className="mt-0.5" />
                  <div className="space-y-1 flex-1">
                    <Label htmlFor="input-channel-active" className="font-medium cursor-pointer">
                      Canal da conversa ativa
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Usa o canal definido pelo gatilho do fluxo (padrão para gatilhos de chat)
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 cursor-pointer">
                  <RadioGroupItem value="specific" id="input-channel-specific" className="mt-0.5" />
                  <div className="space-y-1 flex-1">
                    <Label htmlFor="input-channel-specific" className="font-medium cursor-pointer">
                      Canal específico
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Selecione manualmente o canal (obrigatório para gatilhos CRM)
                    </p>
                  </div>
                </div>
              </RadioGroup>

              {channelMode === 'specific' && (
                <div className="space-y-2 pl-4 border-l-2 border-primary/30">
                  <Label>Inbox para Envio *</Label>
                  <Select
                    value={selectedInboxId}
                    onValueChange={setSelectedInboxId}
                    disabled={loadingInboxes}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione uma inbox..." />
                    </SelectTrigger>
                    <SelectContent>
                      {inboxes.map((inbox: any) => (
                        <SelectItem key={inbox.id} value={inbox.id}>
                          <div className="flex items-center gap-2">
                            <MessageSquare className="h-3 w-3" />
                            <span>{inbox.name}</span>
                            <span className="text-xs text-muted-foreground">
                              ({inbox.channel_type})
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    O canal usado para localizar/criar a conversa e (quando aplicável) enviar a pergunta
                  </p>
                </div>
              )}
            </div>

            {/* RELEASE v1.1.0 - Toggle Apenas aguardar resposta */}
            <Separator className="my-2" />
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg border">
              <div className="space-y-1 flex-1">
                <Label htmlFor="input-wait-only" className="font-medium cursor-pointer">
                  Apenas aguardar resposta (não enviar pergunta)
                </Label>
                <p className="text-xs text-muted-foreground">
                  Útil para gatilhos CRM ou pontos do fluxo em que você quer apenas capturar
                  a próxima mensagem do cliente sem enviar nada. O fluxo pausa aqui e
                  segue para o próximo node assim que o cliente responder.
                </p>
              </div>
              <Switch
                id="input-wait-only"
                checked={waitOnly}
                onCheckedChange={setWaitOnly}
              />
            </div>

            {/* Pergunta — oculta quando wait_only está ativo */}
            {!waitOnly && (
              <div className="space-y-2">
                <Label>Pergunta para o Usuário *</Label>
                <TextareaWithVariables
                  tenantId={tenantId}
                  value={question}
                  onChange={setQuestion}
                  placeholder="Digite a pergunta... Use {{variavel}} para inserir variáveis"
                  maxLength={1024}
                  rows={3}
                />
                <p className="text-xs text-muted-foreground">{question.length}/1024 caracteres</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Mensagem de Validação (opcional)</Label>
              <Input
                placeholder="Mensagem exibida caso o formato esteja incorreto"
                value={validationMessage}
                onChange={(e) => setValidationMessage(e.target.value)}
                maxLength={200}
              />
              <p className="text-xs text-muted-foreground">
                Mensagem personalizada caso o usuário insira um valor inválido
              </p>
            </div>

            {/* RELEASE v1.2.0 - Tempo de espera pela resposta */}
            <Separator className="my-2" />
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4 p-3 rounded-lg border">
                <div className="space-y-1 flex-1">
                  <Label htmlFor="input-timeout-enabled" className="font-medium cursor-pointer">
                    Definir tempo máximo de espera
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Se o contato não responder dentro do tempo, o fluxo avança para o próximo nó.
                    Use um nó de Condição depois para diferenciar resposta vs. timeout.
                  </p>
                </div>
                <Switch
                  id="input-timeout-enabled"
                  checked={timeoutEnabled}
                  onCheckedChange={setTimeoutEnabled}
                />
              </div>

              {timeoutEnabled && (
                <div className="space-y-3 pl-4 border-l-2 border-primary/30">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Aguardar</Label>
                      <Input
                        type="number"
                        min={1}
                        value={timeoutValue}
                        onChange={(e) => setTimeoutValue(Number(e.target.value))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Unidade</Label>
                      <Select value={timeoutUnit} onValueChange={(v: any) => setTimeoutUnit(v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="minutes">Minutos</SelectItem>
                          <SelectItem value="hours">Horas</SelectItem>
                          <SelectItem value="days">Dias</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* v1.3.0 - Variável global de resultado (padrão do node Gerar Cobrança) */}
                  <div className="space-y-2">
                    <Label>Salvar resultado em variável (opcional)</Label>
                    <p className="text-xs text-muted-foreground">
                      Salva o status da espera numa variável global para uso em condições
                      posteriores no fluxo.
                    </p>
                    <VariableDestinationPicker
                      tenantId={tenantId}
                      value={timeoutResultVariable}
                      valueName={timeoutResultVariable}
                      onChange={(variable) => setTimeoutResultVariable(variable?.name || '')}
                      placeholder="Selecione ou crie uma variável..."
                    />

                    {/* Tabela de valores possíveis */}
                    <div className="bg-muted/40 rounded-md p-2.5 space-y-1.5">
                      <span className="text-[10px] uppercase font-semibold text-muted-foreground tracking-wider">
                        Valores possíveis:
                      </span>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-xs">
                          <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                          <code className="bg-emerald-100 dark:bg-emerald-900/30 px-1.5 rounded text-[10px] text-emerald-700 dark:text-emerald-300">responded</code>
                          <span className="text-muted-foreground">— Contato respondeu dentro do tempo</span>
                        </div>
                        <div className="flex items-center gap-2 text-xs">
                          <Clock className="h-3 w-3 text-amber-500" />
                          <code className="bg-amber-100 dark:bg-amber-900/30 px-1.5 rounded text-[10px] text-amber-700 dark:text-amber-300">timeout</code>
                          <span className="text-muted-foreground">— Tempo esgotado sem resposta</span>
                        </div>
                      </div>
                    </div>

                    <p className="text-xs text-muted-foreground">
                      Precisão do disparo é de aproximadamente 1 minuto.
                    </p>
                  </div>

                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave}>
              Salvar Configuração
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="input_data" />
    </>
  );
}
